import { TableCell, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { DateRangeOption } from "@/components/clicks/DateRangeFilter";
import { useDomainPages } from "@/hooks/useClicksData";
import type { UrlActiveFilters } from "@/lib/url-active-filters";

import { CopyableValue } from "./CopyableValue";
import { MetricCell } from "./MetricCell";
import {
  CLICKS_CELL_CLASS,
  gridColumnKey,
  type GridColumn,
  type MetricFieldId,
} from "./metricDisplay";

interface DomainPagesProps {
  domain: string;
  /** The dimension the parent table is grouped by, and the row's own value. */
  dim: string;
  value: string;
  dateRange: DateRangeOption;
  customFrom?: string;
  customTo?: string;
  activeFilters: UrlActiveFilters;
  /** The parent's middle columns, in the same order — so pages line up under it.
   *  Brand/campaign_group belong to the domain, so pages render them blank. */
  columns: GridColumn[];
  /** Trailing (keyword-volume) columns the parent has — blanked here to align. */
  volumeCols: number;
  /** Column count of the parent row, for the single-cell status rows. */
  colSpan: number;
}

/**
 * The pages a domain actually received traffic on, rendered as real rows under
 * the very same columns as the domain above them — same metrics, same order,
 * same Metrics-picker visibility — instead of a separate mini-table. Each page
 * is a full stat row (get_dynamic_stats grouped by url), so every column the
 * parent can show, a page can show too.
 *
 * Takes the same filters as the row above it: without them a country filter
 * would narrow the parent row and not this list, and the pages would refuse to
 * add up to the domain they hang under.
 */
export const DomainPages = ({
  domain,
  dim,
  value,
  dateRange,
  customFrom,
  customTo,
  activeFilters,
  columns,
  volumeCols,
  colSpan,
}: DomainPagesProps) => {
  const { data, isPending, isError } = useDomainPages(
    { domain, dim, value, dateRange, customFrom, customTo, activeFilters },
    true,
  );

  const message = isPending
    ? "Loading…"
    : isError
      ? "Could not load the pages."
      : !data?.length
        ? "No pages for this domain in the selected period."
        : null;

  if (message || !data) {
    return (
      <TableRow className="bg-muted/30 hover:bg-muted/30">
        <TableCell
          colSpan={colSpan}
          className="px-10 py-3 text-sm text-muted-foreground"
        >
          {message}
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {data.map((row, i) => (
        <TableRow
          key={`${row.dimension_value || "-"}|${i}`}
          className="border-b border-border/50 bg-muted/20 hover:bg-muted/40"
        >
          {/* Page URL, indented under the domain. Copyable like the domain is.
              Frozen first column, same as the parent row, so they stay aligned
              during horizontal scroll. */}
          <TableCell
            className={cn(CLICKS_CELL_CLASS, "max-w-[600px]", "sticky left-0 z-10 bg-card")}
            title={row.dimension_value}
          >
            <div className="flex items-center gap-1.5 pl-6">
              <CopyableValue
                value={row.dimension_value}
                placeholder="Unknown"
                className="text-muted-foreground"
              />
            </div>
          </TableCell>

          {/* Same columns, same order as the domain row. Brand, Campaign Group
              and the Domain Tracker fields belong to the domain, not the page —
              keep the cell so columns line up, but leave it blank. */}
          {columns.map((col) =>
            col.kind === "metric" ? (
              <MetricCell
                key={col.metric.id}
                metricId={col.metric.id as MetricFieldId}
                row={row}
                cellClass={CLICKS_CELL_CLASS}
              />
            ) : (
              <TableCell key={gridColumnKey(col)} className={CLICKS_CELL_CLASS} />
            ),
          )}

          {/* Keyword volume is a domain/brand/url attribute we don't fetch per
              page — blank cells so the columns still line up. */}
          {Array.from({ length: volumeCols }, (_, c) => (
            <TableCell key={`v${c}`} className={CLICKS_CELL_CLASS} />
          ))}
        </TableRow>
      ))}
    </>
  );
};
